import { TrashIcon } from '@heroicons/react/24/outline';
import { stringify } from 'query-string';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';

import useDebounce from '@dashboard/hooks/useDebounce';
import { useAdminTemplateCategories } from '@dashboard/hooks/queries/useAdminQueries';
import {
  useUpdateTemplateCategory,
  useDeleteTemplateCategory,
} from '@dashboard/hooks/queries/useAdminMutations';
import useSearchInput from '@dashboard/hooks/useSearchInput';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';

import { confirm } from '@dashboard/lib/confirm';

import Button from '@dashboard/components/Button';
import { DataGrid, DataGridColumnHeader } from '@dashboard/components/DataGrid';
import EmptyState from '@dashboard/components/EmptyState';
import { TextInput } from '@dashboard/components/Form';
import Link from '@dashboard/components/Link';

import Circle from '../components/Circle';

const TemplateCategories = () => {
  const history = useHistory();

  const query = useUrlQuery();

  const { searchInput, setSearchInput, search } = useSearchInput(query.search);
  const [sortBy, setSortBy] = useState(query.sortBy || 'order');
  const [sortDirection, setSortDirection] = useState(
    query.sortDirection ? Number(query.sortDirection) : 1,
  );

  useEffect(() => {
    history.replace({
      search: stringify({
        sortBy,
        sortDirection,
        ...(search && { search }),
      }),
    });
  }, [sortBy, sortDirection, search]);

  // Filters object for the query hook
  const filters = {
    sortBy,
    sortDirection,
    search,
    limit: 20,
  };

  const { data, fetchNextPage, isLoading } = useAdminTemplateCategories(filters, {
    refetchOnMount: true,
  });

  const updateTemplateCategoryMutation = useUpdateTemplateCategory({
    onError: error => {
      window.alert(error.message);
    },
  });

  const deleteTemplateCategoryMutation = useDeleteTemplateCategory({
    onError: error => {
      window.alert(error.message);
    },
  });

  // Convert sortBy/sortDirection to TanStack SortingState
  const sorting = useMemo(
    () => [
      {
        id: sortBy,
        desc: sortDirection === -1,
      },
    ],
    [sortBy, sortDirection],
  );

  // Handle sorting changes from DataGrid
  const handleSortingChange = updater => {
    const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
    if (newSorting.length > 0) {
      const { id, desc } = newSorting[0];
      setSortBy(id);
      setSortDirection(desc ? -1 : 1);
    }
  };

  // Flatten pages data for DataGrid
  const flatData = useMemo(() => data?.pages?.flatMap(page => page.data) ?? [], [data]);

  // Define columns
  const columns = useMemo(
    () => [
      {
        accessorKey: 'title',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Title" />,
        cell: ({ row }) => (
          <Link href={`/admin/template-categories/${row.original._id}`}>
            {row.getValue('title')}
          </Link>
        ),
        enableSorting: true,
      },
      {
        accessorKey: 'enabled',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Enabled" />,
        cell: ({ row }) => <Circle color={row.getValue('enabled') ? 'green' : 'red'} />,
        enableSorting: true,
      },
      {
        accessorKey: 'slug',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Slug" />,
        cell: ({ row }) => row.getValue('slug'),
        enableSorting: true,
      },
      {
        accessorKey: 'order',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Order" />,
        cell: ({ row }) => (
          <OrderCell
            category={row.original}
            onOrderChange={values => {
              updateTemplateCategoryMutation.mutate({
                _id: row.original._id,
                values,
              });
            }}
            onDelete={() => {
              deleteTemplateCategoryMutation.mutate(row.original._id);
            }}
          />
        ),
        enableSorting: true,
      },
    ],
    [updateTemplateCategoryMutation, deleteTemplateCategoryMutation],
  );

  return (
    <div className="pr-4">
      <DataGrid
        columns={columns}
        data={flatData}
        isLoading={isLoading}
        paginationMode="infinite"
        fetchNextPage={fetchNextPage}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        toolbar={{
          showSearch: true,
          searchPlaceholder: 'Search by name',
          customActions: (
            <Button href={`/admin/template-categories/new`} appearance="primary">
              New category
            </Button>
          ),
        }}
        globalFilter={searchInput}
        onGlobalFilterChange={setSearchInput}
        emptyState={
          <EmptyState>
            <EmptyState.Title>No template categories found</EmptyState.Title>
          </EmptyState>
        }
      />
    </div>
  );
};

function OrderCell({ category, onOrderChange, onDelete }) {
  const [order, setOrder] = useState(category.order);
  const debouncedOrder = useDebounce(order, 300);

  useEffect(() => {
    if (debouncedOrder === category.order) return;

    onOrderChange({
      order: debouncedOrder,
    });
  }, [debouncedOrder]);

  return (
    <div className="flex">
      <TextInput
        type="number"
        value={order}
        onChange={e => {
          setOrder(e.target.value);
        }}
      />

      <Button
        onClick={async () => {
          const proceed = await confirm('Delete this template?');

          if (!proceed) return;

          onDelete();
        }}
        appearance="bare"
        className="ml-2"
        size="small"
      >
        <TrashIcon className="w-4 h-4" />
      </Button>
    </div>
  );
}

export default TemplateCategories;
